import type { HomeyAPI } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { ToastManager } from '../../../ToastManager.jsx';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectBase } from '../../../store/selectors.ts';
import { useAttach } from '../../../store/useAttach';

type UpdateItem = { changelog: string; date: string; size: number; version: string };
type UpdatesInfo = UpdateItem[];

type UpdateStateData = {
  downloading: boolean;
  downloads_progress: number | null;
  installing: boolean;
  error: string | null;
};

interface State {
  updateData: UpdateItem | null;
  updateStateData: UpdateStateData | null;
  optionAutoupdateData: boolean | null;
  isLoading: boolean;
  isRefetchingUpdates: boolean;
  error: unknown;
}

type UpdatesSettingsStoreState = State;

class UpdatesSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('updates-settings');
  }

  override createInitialState() {
    return {
      updateData: null,
      updateStateData: null,
      optionAutoupdateData: false,
      stateData: null,
      isLoading: true,
      isRefetchingUpdates: false,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      const updatePromise = api.updates.getUpdates({
        cache: false,
      }) as unknown as Promise<UpdatesInfo>;

      let optionAutoupdatePromise;

      if (api.homey.model === 'shs') {
        optionAutoupdatePromise = Promise.resolve({ value: null });
      } else {
        optionAutoupdatePromise = api.updates.getOptionAutoupdate() as unknown as Promise<{
          value: boolean;
        }>;
      }
      const updateStatePromise = api.updates.getState() as unknown as Promise<UpdateStateData>;

      const [updateData, optionAutoupdateData, updateStateData] = await Promise.all([
        updatePromise,
        optionAutoupdatePromise,
        updateStatePromise,
      ]);

      if (abortController.signal.aborted) {
        return;
      }

      this.set({
        ...this.createInitialState(),
        updateData: updateData[updateData.length - 1] ?? null,
        updateStateData: updateStateData,
        optionAutoupdateData: optionAutoupdateData.value,
        isLoading: false,
      });
      await this.connectManager({ api, manager: api.updates, reference: this });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      if (fetchPolicy !== 'background') {
        ToastManager.handleError(error);
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      if (api.updates != null) {
        this.disconnectManager({ api, manager: api.updates, reference: this }).catch(console.error);
      }
    } catch (error) {}
  }

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, this.key);

    return { updatesSettings: this.store(selectAll, shallow) };
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.updates?.on('state', this.onState);
    api.updates?.on('download_start', this.onDownloadStart);
    api.updates?.on('download_progress', this.onDownloadProgress);
    api.updates?.on('download_done', this.onDownloadDone);
    api.updates?.on('download_error', this.onDownloadError);
    api.updates?.on('install_start', this.onInstallStart);
    api.updates?.on('install_done', this.onInstallDone);
    api.updates?.on('install_error', this.onInstallError);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.updates?.off('state', this.onState);
    api.updates?.off('download_start', this.onDownloadStart);
    api.updates?.off('download_progress', this.onDownloadProgress);
    api.updates?.off('download_done', this.onDownloadDone);
    api.updates?.off('download_error', this.onDownloadError);
    api.updates?.off('install_start', this.onInstallStart);
    api.updates?.off('install_done', this.onInstallDone);
    api.updates?.off('install_error', this.onInstallError);
  }

  private onState = (state: { downloading: boolean; installing: boolean; error: null }) => {
    console.log('onState');
    console.log(state);

    this.set((prevState) => {
      if (prevState.updateStateData == null) return {};

      return {
        updateStateData: { ...prevState.updateStateData, ...state },
      };
    });
  };

  private onDownloadStart = () => {
    console.log('onDownloadStart');
  };

  private onDownloadProgress = ({ percent }: { percent: number }) => {
    console.log('onDownloadProgress');
    this.set((prevState) => {
      if (prevState.updateStateData == null) return {};

      return {
        updateStateData: { ...prevState.updateStateData, downloads_progress: percent / 100 },
      };
    });
  };

  private onDownloadDone = () => {
    console.log('onDownloadDone');
    this.emitter.emit('download_done');
  };

  private onDownloadError = ({ message }: { message: string }) => {
    console.log('onDownloadError', message);
    this.set((prevState) => {
      if (prevState.updateStateData == null) return {};

      return {
        updateStateData: { ...prevState.updateStateData, error: message },
      };
    });
  };

  private onInstallStart = () => {
    console.log('onInstallStart');
  };

  private onInstallDone = ({ bootId }: { bootId: string }) => {
    console.log('onInstallDone', { bootId });
    this.emitter.emit('install_done', { bootId });
  };

  // Only on platform version 1.
  private onInstallError = ({ message }: { message: string }) => {
    console.log('onInstallError');
    this.set((prevState) => {
      if (prevState.updateStateData == null) return {};

      return {
        updateStateData: { ...prevState.updateStateData, error: message },
      };
    });
  };

  public async installUpdate() {
    const api = this.getApi();
    await api.updates.installUpdate({});
  }

  public async refetchUpdates() {
    const api = this.getApi();
    const state = this.get();

    if (state.isRefetchingUpdates === true) return;

    this.set({
      isRefetchingUpdates: true,
    });

    let updateData;

    try {
      updateData = (await api.updates.getUpdates({ cache: false })) as unknown as UpdatesInfo;
    } catch (error) {
      this.set({
        isRefetchingUpdates: false,
      });
      throw error;
    }

    this.set({
      updateData: updateData[0],
      isRefetchingUpdates: false,
    });

    return updateData;
  }
}

function selectAll(state: UpdatesSettingsStoreState) {
  return {
    ...selectBase(state),
    updateData: state.updateData,
    optionAutoupdateData: state.optionAutoupdateData,
    updateStateData: state.updateStateData,
    isRefetchingUpdates: state.isRefetchingUpdates,
  };
}

export const updatesSettingsStore = new UpdatesSettingsStore();
